import { useState, useEffect } from 'react';
import axios from 'axios';
import { Film } from 'lucide-react';

const API = '/api';

/**
 * A note, where the issue is actually composed, that the Hub is holding film.
 *
 * WHY A NOTE AND NOT AN INSERTION. Nothing here writes into an issue. The films are candidate
 * material; whether a poster and a watch link belong in a given issue is an editorial call,
 * and a panel that made it would be scheduling content by side effect. So this says what
 * exists and where to get it, and stops.
 *
 * IT IS DERIVED, NEVER A STORED FLAG. It reads the same endpoint the image picker reads, so
 * it appears the moment Randall attaches a film and disappears by itself when the library
 * holds none — nobody clears anything. A hardcoded "we have two Fly Series films" would still
 * be on this screen in December.
 *
 * The dated prompt has the same problem one layer down, so the dates are dropped once they
 * are past rather than left to rot into noise (CLAUDE.md 14).
 *
 * Props:
 *   where: 't2t' | 'weekly-fitter' — which issues this note points at
 *   deadlines: ISO dates the note names while they are still ahead
 */
export default function VideoCandidatesNote({ where = 't2t', deadlines = [] }) {
  const [films, setFilms] = useState([]);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/email/library-images`)
      .then((res) => { if (live) setFilms(res.data.video_posters || []); })
      // A note is additive. If the lookup fails the composer is unaffected — and an empty
      // note is not a claim that there is no film, it is simply no note.
      .catch(() => {});
    return () => { live = false; };
  }, []);

  if (!films.length) return null;

  const today = new Date().toISOString().slice(0, 10);
  const ahead = deadlines.filter((d) => d >= today);
  const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const target = where === 'weekly-fitter'
    ? (ahead.length ? `the ${ahead.map(fmt).join(' and ')} Weekly Fitters` : 'the next Weekly Fitter')
    : 'the next Teacher-to-Teacher issue';

  return (
    <div
      data-testid="video-candidates-note"
      className="flex items-start gap-2 text-[11px] font-sans text-hg-text-dim bg-hg-surface border border-hg-border rounded-sm px-3 py-2"
    >
      <Film size={13} className="shrink-0 mt-0.5 text-hg-green" />
      <div>
        <span className="text-hg-text">
          {films.length} film{films.length === 1 ? '' : 's'} in the library
        </span>{' '}
        — candidate material for {target}. Each carries a poster still and a watch link; pick
        one under <span className="text-hg-text">Library</span> on any image slot. Nothing is
        inserted for you.
        <div className="mt-0.5 truncate">{films.map((f) => f.filename || f.public_id).join(' · ')}</div>
      </div>
    </div>
  );
}
